import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Snowflake, ShieldCheck, Clock, MapPin, ArrowRight, Truck, Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_lucas-trucking/artifacts/mu1jcjql_IMG_5465.jpeg";
const ABOUT_IMG = "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const FEATURES = [
  { icon: Snowflake, title: "Temperature Controlled", sub: "From pickup to delivery" },
  { icon: ShieldCheck, title: "Safe & Secure", sub: "Your freight, our priority" },
  { icon: Clock, title: "On Time", sub: "Reliable nationwide service" },
  { icon: MapPin, title: "Nationwide", sub: "Refrigerated freight solutions" },
];

const SERVICE_DEFAULTS = [
  { title: "Refrigerated Freight", icon: Snowflake, img: "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=800", desc: "Temperature-controlled transportation for perishable goods, ensuring your cargo arrives fresh and secure." },
  { title: "Time-Sensitive Delivery", icon: Clock, img: "https://images.pexels.com/photos/10359129/pexels-photo-10359129.jpeg?auto=compress&cs=tinysrgb&w=800", desc: "On-time, dependable service for time-critical and high-value shipments." },
  { title: "Safety & Compliance", icon: ShieldCheck, img: "https://images.pexels.com/photos/6563903/pexels-photo-6563903.jpeg?auto=compress&cs=tinysrgb&w=800", desc: "Fully compliant and committed to the highest safety standards in the trucking industry." },
  { title: "Professional Fleet", icon: Truck, img: "https://images.pexels.com/photos/35602229/pexels-photo-35602229.jpeg?auto=compress&cs=tinysrgb&w=800", desc: "Well-maintained, modern equipment delivering reliable performance across the country." },
];

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

export default function Home() {
  const [fleet, setFleet] = useState([]);
  const { info = {} } = useOutletContext() || {};

  useEffect(() => {
    api.get("/fleet-categories?active_only=true").then((r) => setFleet(r.data)).catch(() => {});
  }, []);

  const heroImg = resolveUrl(info.hero_image_url) || HERO_IMG;
  const aboutImg = resolveUrl(info.home_about_image_url) || ABOUT_IMG;
  const services = SERVICE_DEFAULTS.map((s, i) => ({
    ...s,
    img: resolveUrl(info[`service_${i + 1}_image_url`]) || s.img,
  }));

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" data-lt-scheme="dark" className="relative overflow-hidden bg-[color:var(--lt-navy)]">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Refrigerated freight truck" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,25,47,0.92) 0%, rgba(8,25,47,0.75) 40%, rgba(8,25,47,0.35) 80%, rgba(8,25,47,0.55) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:py-40">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-3 text-white/90 text-xs md:text-sm font-semibold tracking-[0.3em] uppercase mb-6">
              <span className="h-px w-10 bg-[color:var(--lt-accent-bright)]" />
              Temperature Controlled · Nationwide
            </div>
            <h1 className="font-italic-display uppercase text-white leading-[0.9] text-6xl md:text-7xl lg:text-8xl" data-testid="hero-headline">
              A COOLER<br />
              <span className="bg-gradient-to-r from-[#3B82F6] to-[#93C5FD] bg-clip-text text-transparent">TOMORROW</span>
            </h1>
            <div className="mt-6 inline-flex items-center gap-3 text-white text-sm tracking-[0.35em] font-semibold uppercase">
              <span>Safe</span><span className="text-[color:var(--lt-accent-bright)]">•</span>
              <span>Reliable</span><span className="text-[color:var(--lt-accent-bright)]">•</span>
              <span>On Time</span>
            </div>
            <p className="mt-6 text-white/85 text-lg max-w-xl">
              {info.hero_subheadline || "Refrigerated freight transportation that keeps your business moving."}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/quote">
                <Button data-testid="hero-quote-btn" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white h-14 pl-6 pr-7 rounded-full font-bold uppercase tracking-wider shadow-[0_10px_28px_-8px_var(--lt-accent)]">
                  Get a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-contact-btn" variant="outline" className="h-14 px-6 rounded-full border-white/50 bg-transparent text-white hover:bg-white hover:text-[color:var(--lt-navy)] font-semibold">
                  <Phone className="h-4 w-4 mr-2" /> Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INFO STRIP */}
      <section data-lt-scheme="dark" className="bg-[color:var(--lt-navy)] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-center gap-4 px-3 md:px-6 py-6" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="shrink-0 h-11 w-11 rounded-full bg-white/10 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold uppercase tracking-wider text-sm">{f.title}</div>
                <div className="text-white/60 text-xs mt-0.5">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
        {/* MC / DOT bar */}
        <div className="border-t border-white/10 bg-[color:var(--lt-navy-deep)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-sm text-white/80">
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[color:var(--lt-accent-bright)]" /> {info.address_line || "Hoffman Estates, IL"}</span>
            {info.mc_number && <span className="inline-flex items-center gap-2">MC# {info.mc_number}</span>}
            {info.dot_number && <span className="inline-flex items-center gap-2">DOT# {info.dot_number}</span>}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 text-[color:var(--lt-accent)] text-xs font-bold tracking-[0.35em] uppercase mb-3">
              <span className="h-px w-8 bg-[color:var(--lt-accent)]" />
              Our Services
              <span className="h-px w-8 bg-[color:var(--lt-accent)]" />
            </div>
            <h2 className="font-italic-display uppercase text-4xl md:text-5xl leading-none text-[color:var(--lt-navy)]">
              Keeping America&apos;s Supply Chain <span className="text-[color:var(--lt-accent)]">Cold</span>
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Reliable refrigerated transportation solutions for your business.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon || Truck;
              return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl overflow-hidden border border-[var(--lt-border)] shadow-[0_10px_30px_-15px_var(--lt-shadow)] hover:shadow-[0_20px_40px_-15px_var(--lt-shadow)] transition-shadow"
                data-testid={`service-tile-${i}`}
              >
                <div className="relative h-40">
                  <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-6 h-12 w-12 rounded-full bg-[color:var(--lt-accent)] flex items-center justify-center ring-4 ring-white shadow-lg">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="p-6 pt-9 text-center">
                  <h3 className="font-bold text-lg text-[color:var(--lt-navy)]">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-lt-scheme="dark" className="relative bg-[color:var(--lt-navy)] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutImg} alt="" className="absolute right-0 top-0 h-full w-full md:w-1/2 object-cover opacity-70" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,25,47,0.98) 0%, rgba(8,25,47,0.95) 45%, rgba(8,25,47,0.35) 100%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-xl">
            <div className="text-[color:var(--lt-accent-bright)] text-xs font-bold tracking-[0.35em] uppercase mb-3">About Lucas Trans Inc.</div>
            <h2 className="font-italic-display uppercase text-4xl md:text-5xl leading-[0.95]">
              A Trusted<br />Refrigerated<br />Trucking Partner
            </h2>
            <p className="mt-6 text-white/85 leading-relaxed">
              {info.about_story || "Lucas Trans Inc. is a refrigerated trucking company based in Hoffman Estates, IL, providing reliable, temperature-controlled transportation across the United States. We are committed to safety, on-time delivery, and building long-term partnerships with our customers."}
            </p>
            <div className="mt-8">
              <Link to="/about">
                <Button data-testid="about-cta" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white h-12 pl-5 pr-6 rounded-full font-semibold">
                  Learn More About Us <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { icon: ShieldCheck, label: "Reliable Service" },
              { icon: ShieldCheck, label: "Safety Focused" },
              { icon: Truck, label: "Modern Fleet" },
              { icon: Snowflake, label: "Long-Term Partnerships" },
            ].map((s) => (
              <div key={s.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 flex flex-col items-center text-center">
                <s.icon className="h-6 w-6 text-[color:var(--lt-accent-bright)] mb-2" />
                <div className="text-xs font-semibold uppercase tracking-wider text-white/90">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white py-16 md:py-20 border-b border-[var(--lt-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <div>
            <div className="text-[color:var(--lt-accent)] text-xs font-bold tracking-[0.35em] uppercase mb-3">Get in Touch</div>
            <h2 className="font-italic-display uppercase text-4xl md:text-5xl leading-none text-[color:var(--lt-navy)]">
              Let&apos;s Move Your<br />Business Forward
            </h2>
            <p className="mt-4 text-slate-600 max-w-lg">Request a quote or get in touch with our team. We&apos;re here to help with your refrigerated freight needs.</p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/quote">
              <Button data-testid="cta-quote" className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white h-14 pl-5 pr-7 rounded-full font-semibold">
                <Truck className="h-4 w-4 mr-2" /> Get a Quote <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="cta-contact" variant="outline" className="h-14 px-6 rounded-full border-[color:var(--lt-navy)]/30 text-[color:var(--lt-navy)] hover:bg-[color:var(--lt-navy)] hover:text-white font-semibold">
                <Phone className="h-4 w-4 mr-2" /> Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FLEET STRIP — keeps admin-driven data alive */}
      {fleet.length > 0 && (
        <section className="bg-[var(--lt-panel-hover)] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="text-[color:var(--lt-accent)] text-xs font-bold tracking-[0.35em] uppercase mb-3">Our Fleet</div>
              <h3 className="font-italic-display uppercase text-3xl md:text-4xl text-[color:var(--lt-navy)]">Equipment We Operate</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {fleet.slice(0, 4).map((f) => (
                <Link to="/fleet" key={f.id} className="group bg-white rounded-xl overflow-hidden border border-[var(--lt-border)] shadow-sm hover:shadow-lg transition-shadow">
                  <div className="relative h-36 overflow-hidden">
                    <img src={f.image_url} alt={f.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-[color:var(--lt-navy)]">{f.name}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{f.typical_freight}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
